import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { ConnectivityService } from '../service/connectivity.service';
import { IndexedDbStorageService } from '../service/indexed-db-storage.service';
import { MicrosoftAuthConfig, authRedirectUri, authSettings } from './auth.config';

const RETURN_URL_KEY = 'tops.auth.returnUrl';
const AUTH_STATE_KEY = 'tops.auth.state';
const AUTH_SESSION_KEY = 'tops.auth.session';
const OFFLINE_AUTH_SESSION_KEY = 'tops.auth.offlineSession';
const OFFLINE_SESSION_TTL_MS = 30 * 24 * 60 * 60 * 1000;

interface AuthAccount {
  name?: string;
  username: string;
}

interface AuthSession {
  account: AuthAccount;
  accessToken: string;
  expiresAt: number;
}

interface OfflineAuthSession {
  schemaVersion: 1;
  account: AuthAccount;
  lastLoginAt: number;
  expiresAt: number;
}

interface EncryptedSessionPayload {
  iv: string;
  data: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly connectivity = inject(ConnectivityService);
  private readonly storage = inject(IndexedDbStorageService);
  private readonly accountSignal = signal<AuthAccount | null>(null);
  private session: AuthSession | null = null;
  private authenticatedFromOfflineCache = false;
  private initialized = false;
  private initializationPromise: Promise<void> | null = null;
  private authConfigPromise: Promise<MicrosoftAuthConfig> | null = null;

  constructor() {
    this.connectivity.isOnline$.subscribe((isOnline) => {
      if (isOnline) void this.refreshOfflineSessionWhenOnline();
    });
  }

  readonly account = this.accountSignal.asReadonly();
  readonly isAuthenticated = computed(() => !!this.accountSignal());

  async initialize(): Promise<void> {
    if (this.initialized) return;
    if (this.initializationPromise) return this.initializationPromise;

    this.initializationPromise = this.initializeSession();
    return this.initializationPromise;
  }

  private async initializeSession(): Promise<void> {
    await this.loadStoredSession();

    if (this.isRedirectCallbackRoute()) {
      await this.completeRedirectCallback();
      return;
    }

    this.initialized = true;
  }

  private isRedirectCallbackRoute(): boolean {
    return window.location.pathname.toLowerCase() === authSettings.redirectPath.toLowerCase();
  }

  private async loadStoredSession(): Promise<void> {
    await this.storage.ready();
    const now = Date.now();
    const rawSession = this.storage.getItem(AUTH_SESSION_KEY);
    if (!rawSession) {
      await this.loadOfflineSession(now);
      return;
    }

    try {
      const session = JSON.parse(rawSession) as AuthSession;
      if (!session?.accessToken || !this.validAccount(session.account) || now >= session.expiresAt) {
        this.storage.removeItem(AUTH_SESSION_KEY);
        await this.loadOfflineSession(now);
        return;
      }
      this.session = session;
      this.authenticatedFromOfflineCache = false;
      this.accountSignal.set(session.account);
    } catch {
      this.storage.removeItem(AUTH_SESSION_KEY);
      await this.loadOfflineSession(now);
    }
  }

  private async loadOfflineSession(now = Date.now()): Promise<void> {
    if (this.connectivity.currentOnline) return;

    const offlineSession = await this.readOfflineSession();
    if (!offlineSession) return;

    if (!this.validAccount(offlineSession.account) || now >= offlineSession.expiresAt) {
      this.storage.removeItem(OFFLINE_AUTH_SESSION_KEY);
      return;
    }

    this.session = null;
    this.authenticatedFromOfflineCache = true;
    this.accountSignal.set(offlineSession.account);
  }

  private async completeRedirectCallback(): Promise<void> {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    if (error) {
      await this.failCallback(params.get('error_description') || error);
      return;
    }

    const code = params.get('code');
    if (!code) {
      if (this.session) await this.goToReturnUrl();
      this.initialized = true;
      return;
    }

    const expectedState = sessionStorage.getItem(AUTH_STATE_KEY);
    const actualState = params.get('state');
    if (expectedState && actualState !== expectedState) {
      await this.failCallback('Microsoft sign-in state did not match. Please try again.');
      return;
    }

    let session: AuthSession;
    try {
      session = await firstValueFrom(this.http.post<AuthSession>(`${environment.backendApiBaseUrl}/auth/microsoft/callback`, {
        code,
        redirectUri: authRedirectUri,
      }));
    } catch (exchangeError) {
      await this.failCallback(this.authErrorMessage(exchangeError));
      return;
    }

    this.session = session;
    this.authenticatedFromOfflineCache = false;
    this.accountSignal.set(session.account);
    this.storage.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
    await this.storeOfflineSession(session.account);
    sessionStorage.removeItem(AUTH_STATE_KEY);
    this.initialized = true;
    await this.goToReturnUrl();
  }

  private async failCallback(message: string): Promise<void> {
    sessionStorage.removeItem(AUTH_STATE_KEY);
    sessionStorage.setItem('tops.auth.error', message);
    this.initialized = true;
    await this.router.navigate(['/login'], { queryParams: { returnUrl: sessionStorage.getItem(RETURN_URL_KEY) || '/' } });
  }

  private authErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const serverMessage = typeof error.error?.message === 'string' ? error.error.message : '';
      return serverMessage || error.message || 'Microsoft sign-in failed.';
    }
    return error instanceof Error ? error.message : 'Microsoft sign-in failed.';
  }

  private async goToReturnUrl(): Promise<void> {
    const returnUrl = sessionStorage.getItem(RETURN_URL_KEY) || '/';
    sessionStorage.removeItem(RETURN_URL_KEY);
    await this.router.navigateByUrl(returnUrl);
  }

  login(returnUrl = '/'): void {
    void this.startLogin(returnUrl);
  }

  private async startLogin(returnUrl = '/'): Promise<void> {
    const config = await this.microsoftAuthConfig();
    sessionStorage.setItem(RETURN_URL_KEY, returnUrl);
    const state = crypto.randomUUID();
    sessionStorage.setItem(AUTH_STATE_KEY, state);

    const params = new URLSearchParams({
      client_id: config.clientId,
      response_type: 'code',
      redirect_uri: authRedirectUri,
      response_mode: 'query',
      scope: config.scope || authSettings.scopes.join(' '),
      state,
    });

    window.location.assign(`${config.authority}/oauth2/v2.0/authorize?${params.toString()}`);
  }

  async logout(): Promise<void> {
    await this.storage.ready();
    this.storage.removeItem(AUTH_SESSION_KEY);
    this.storage.removeItem(OFFLINE_AUTH_SESSION_KEY);
    sessionStorage.removeItem(AUTH_STATE_KEY);
    sessionStorage.removeItem(RETURN_URL_KEY);
    this.session = null;
    this.authenticatedFromOfflineCache = false;
    this.accountSignal.set(null);
    const config = await this.microsoftAuthConfig();
    const params = new URLSearchParams({ post_logout_redirect_uri: `${window.location.origin}/login` });
    window.location.assign(`${config.authority}/oauth2/v2.0/logout?${params.toString()}`);
  }

  async accessToken(): Promise<string | null> {
    await this.initialize();
    return this.session && Date.now() < this.session.expiresAt ? this.session.accessToken : null;
  }

  async restoreOfflineSession(): Promise<boolean> {
    await this.storage.ready();
    await this.loadOfflineSession();
    return !!this.accountSignal();
  }

  private async refreshOfflineSessionWhenOnline(): Promise<void> {
    if (!this.initialized || !this.authenticatedFromOfflineCache || this.isRedirectCallbackRoute()) return;
    const returnUrl = `${window.location.pathname}${window.location.search}${window.location.hash}` || '/';
    this.login(returnUrl);
  }

  private async storeOfflineSession(account: AuthAccount): Promise<void> {
    if (!this.validAccount(account)) return;

    const now = Date.now();
    const offlineSession: OfflineAuthSession = {
      schemaVersion: 1,
      account,
      lastLoginAt: now,
      expiresAt: now + OFFLINE_SESSION_TTL_MS,
    };

    const encryptedSession = await this.encryptOfflineSession(offlineSession);
    if (encryptedSession) this.storage.setItem(OFFLINE_AUTH_SESSION_KEY, encryptedSession);
  }

  private async readOfflineSession(): Promise<OfflineAuthSession | null> {
    const encryptedSession = this.storage.getItem(OFFLINE_AUTH_SESSION_KEY);
    if (!encryptedSession) return null;

    try {
      return await this.decryptOfflineSession(encryptedSession);
    } catch {
      this.storage.removeItem(OFFLINE_AUTH_SESSION_KEY);
      return null;
    }
  }

  private async encryptOfflineSession(session: OfflineAuthSession): Promise<string | null> {
    if (!crypto?.subtle) return null;

    const iv = crypto.getRandomValues(new Uint8Array(12));
    const key = await this.offlineEncryptionKey();
    const encodedSession = new TextEncoder().encode(JSON.stringify(session));
    const encryptedBytes = new Uint8Array(await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: this.bytesToArrayBuffer(iv) },
      key,
      this.bytesToArrayBuffer(encodedSession),
    ));
    const payload: EncryptedSessionPayload = {
      iv: this.bytesToBase64(iv),
      data: this.bytesToBase64(encryptedBytes),
    };

    return JSON.stringify(payload);
  }

  private async decryptOfflineSession(encryptedSession: string): Promise<OfflineAuthSession | null> {
    if (!crypto?.subtle) return null;

    const payload = JSON.parse(encryptedSession) as EncryptedSessionPayload;
    if (!payload?.iv || !payload?.data) return null;

    const key = await this.offlineEncryptionKey();
    const iv = this.bytesToArrayBuffer(this.base64ToBytes(payload.iv));
    const data = this.bytesToArrayBuffer(this.base64ToBytes(payload.data));
    const decryptedBytes = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, data);
    return JSON.parse(new TextDecoder().decode(decryptedBytes)) as OfflineAuthSession;
  }

  private async offlineEncryptionKey(): Promise<CryptoKey> {
    const material = `${window.location.origin}|tops-offline-auth-v1`;
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(material));
    return crypto.subtle.importKey('raw', digest, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
  }

  private microsoftAuthConfig(): Promise<MicrosoftAuthConfig> {
    if (!this.authConfigPromise) {
      this.authConfigPromise = firstValueFrom(this.http.get<MicrosoftAuthConfig>(`${environment.backendApiBaseUrl}/auth/microsoft/config`));
    }
    return this.authConfigPromise;
  }

  private validAccount(account: AuthAccount | null | undefined): account is AuthAccount {
    return !!account?.username;
  }

  private bytesToBase64(bytes: Uint8Array): string {
    let binary = '';
    bytes.forEach((byte) => binary += String.fromCharCode(byte));
    return btoa(binary);
  }

  private base64ToBytes(value: string): Uint8Array {
    const binary = atob(value);
    return Uint8Array.from(binary, (character) => character.charCodeAt(0));
  }

  private bytesToArrayBuffer(bytes: Uint8Array): ArrayBuffer {
    return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
  }
}